import { useEffect, useState } from 'react'
import styles from './langSelectDropdown.module.css'
import { useLayoutContext } from '../../contexts/LayoutContext'
import { getSpeechCapabilities } from '../../api'
import { SpeechLocale } from '../../api'
import { Dismiss20Filled } from '@fluentui/react-icons'

type LangSelectDropdownProps = {
    micLanguageLocale: string
    setMicLanguageLocale: (lang: string) => void
    micLanguageDisplayName?: string
    setMicLanguageDisplayName?: (lang: string) => void
    isLanguageModalOpen: boolean
    setIsLanguageModalOpen: React.Dispatch<React.SetStateAction<boolean>>
    isRecording: boolean
    micPermissionsDenied: boolean | undefined
}

interface QuestionInputOutletContext {
    isMobile: boolean
}

function LangSelectDropdown({
    micLanguageLocale,
    setMicLanguageLocale,
    isLanguageModalOpen,
    setIsLanguageModalOpen,
    isRecording,
    micPermissionsDenied,
}: LangSelectDropdownProps) {
    const [availableLanguages, setAvailableLanguages] = useState<
        SpeechLocale[]
    >([])
    // The sentinel that asks the provider to identify the language. Held in state
    // because the backend publishes it: this menu used to send '' while the admin
    // frontend sent 'auto', and only one of them was a value the API understood.
    const [autoDetect, setAutoDetect] = useState('auto')
    // Initialise selection to match the current locale (empty string = Auto-detect).
    const [selectedButton, setSelectedButton] =
        useState<string>(micLanguageLocale)
    const [isLoadingMicSetting, setIsLoadingMicSetting] = useState(true)
    const { isMobile } = useLayoutContext()
    useEffect(() => {
        const fetchAvailableLanguages = async () => {
            // One list, published by the backend. This used to fetch 146 locales
            // and filter them on `fast_transcription_support` — a field the API
            // serialises as `fastTranscriptionSupport`, so every entry read
            // `undefined` and this menu rendered empty. The tenant flag that
            // bypassed the filter showed all 146, of which 8 were transcribable.
            const capabilities = await getSpeechCapabilities()
            setAutoDetect(capabilities.autoDetect)
            setAvailableLanguages(
                capabilities.locales.filter(
                    (locale) => locale.value !== capabilities.autoDetect
                )
            )
        }
        fetchAvailableLanguages()
    }, [])

    let selected
    for (let i = 0; i < availableLanguages.length; i++) {
        if (availableLanguages[i].value === micLanguageLocale) {
            selected = availableLanguages[i]
        }
    }

    const handleLanguageChange = (lang: string) => {
        setMicLanguageLocale(lang)
        setSelectedButton(lang)
    }

    return (
        micPermissionsDenied === false &&
        isMobile === true &&
        isLanguageModalOpen === true &&
        isRecording === false && (
            <div className={styles.container}>
                <div className={styles.langDropdownModalContainer}>
                    <button
                        className={styles.closeButton}
                        onClick={() => setIsLanguageModalOpen(false)}
                    >
                        <Dismiss20Filled
                            style={{
                                display: 'block',
                                lineHeight: 1,
                            }}
                        />
                    </button>
                    <div className={styles.modalContent}>
                        <div className={styles.title}>
                            <h1>Speech input language</h1>
                        </div>
                        <div className={styles.dropdownMenu}>
                            {/* Auto-detect asks the provider to identify the
                                language. Kept hardcoded rather than rendered from
                                the fetched list so it survives a failed fetch —
                                but its value now comes from the backend, because
                                this button used to send '' where the API's
                                sentinel is 'auto'. */}
                            <button
                                className={
                                    selectedButton === autoDetect
                                        ? styles.dropdownItemSelected
                                        : styles.dropdownItem
                                }
                                onClick={() => handleLanguageChange(autoDetect)}
                            >
                                Auto-detect
                            </button>
                            {availableLanguages.map((lang) => (
                                <button
                                    key={lang.value}
                                    value={lang.value}
                                    className={
                                        selectedButton === lang.value
                                            ? styles.dropdownItemSelected
                                            : styles.dropdownItem
                                    }
                                    onClick={() =>
                                        handleLanguageChange(lang.value)
                                    }
                                >
                                    {lang.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        )
    )
}

export default LangSelectDropdown
